/**
 * PendingSyncIndicator — small AppBar widget showing how many shop-floor
 * actions are queued waiting for the network (M4 / Step 6.4).
 *
 *   - Renders nothing on web (the queue is a no-op there per Step 13).
 *   - Renders nothing on desktop when the queue is empty.
 *   - Clicking opens a popover listing the pending items with their last
 *     error, attempt count, and operator-override actions (Retry / Discard).
 *
 * The indicator is purely informational — operators do not need to take
 * action; the queue retries on its own with exponential backoff. The
 * controls exist so a support engineer can intervene when an item is stuck
 * in `awaiting_reauth` or `failed`.
 */

import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import { usePendingActions } from '../hooks/usePendingActions';
import { isDesktop } from '../services/platformBridge';
import type { ActionQueueRecordStatus } from '@forgedas/shared-core';

const PRIMARY = '#1F7A63';

function statusLabel(status: ActionQueueRecordStatus): { label: string; color: string } {
  switch (status) {
    case 'pending':
      return { label: 'Queued', color: '#9CA3AF' };
    case 'in_flight':
      return { label: 'Sending…', color: PRIMARY };
    case 'awaiting_reauth':
      return { label: 'Sign in to retry', color: '#F59E0B' };
    case 'failed':
      return { label: 'Failed', color: '#EF4444' };
    case 'delivered':
      return { label: 'Delivered', color: '#10B981' };
    default:
      return { label: status, color: '#9CA3AF' };
  }
}

function formatAge(createdAt: number): string {
  const ageMs = Date.now() - createdAt;
  const secs = Math.floor(ageMs / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export const PendingSyncIndicator: React.FC = () => {
  const { stats, pending, online, retry, discard } = usePendingActions();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const visibleCount = stats.pending + stats.awaitingReauth + stats.failed;
  const showIndicator = isDesktop && visibleCount > 0;

  const sortedPending = useMemo(() => {
    return [...pending].sort((a, b) => a.createdAt - b.createdAt);
  }, [pending]);

  if (!showIndicator) return null;

  const tooltip = (() => {
    const parts: string[] = [];
    if (!online) parts.push('offline');
    if (stats.pending) parts.push(`${stats.pending} waiting`);
    if (stats.awaitingReauth) parts.push(`${stats.awaitingReauth} need sign-in`);
    if (stats.failed) parts.push(`${stats.failed} failed`);
    return parts.join(' · ') || 'All actions synced';
  })();

  const badgeColor: 'warning' | 'error' | 'default' =
    stats.failed > 0 ? 'error' : stats.awaitingReauth > 0 ? 'warning' : 'default';

  /* ══════════════════════════════════════════════════════════════════════════
     T83 — THE CLOUD ICON NOW MEANS WHAT IT LOOKS LIKE IT MEANS.

     This read `stats.flushing ? CloudSync : CloudOff`. `flushing` is true only
     while the drain loop is mid-pass — a fraction of a second, once every thirty
     — so the slashed cloud was shown essentially 100% of the time and had NOTHING
     to do with the network. The owner read it, correctly, as "always offline".

     Three states, each answering a different question:
       CloudOff   — we have positive evidence there is no connection.
       CloudSync  — a drain is in progress right now.
       CloudQueue — connected, with work still waiting (or held for a decision).
     "Connected and nothing waiting" is not a state, because the widget is hidden
     then; that is unchanged.
     ══════════════════════════════════════════════════════════════════════════ */
  const Icon = !online ? CloudOffIcon : stats.flushing ? CloudSyncIcon : CloudQueueIcon;

  const handleRetry = async (id: string): Promise<void> => {
    setBusyId(id);
    try {
      await retry(id);
    } finally {
      setBusyId(null);
    }
  };

  const handleDiscard = async (id: string): Promise<void> => {
    setBusyId(id);
    try {
      await discard(id);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Tooltip title={`Pending sync — ${tooltip}`}>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            color: 'var(--text-secondary)',
            ml: 1,
            transition: 'all 0.15s ease',
            '&:hover': { bgcolor: 'var(--bg-sidebar-active)', color: PRIMARY },
          }}
        >
          <Badge
            badgeContent={visibleCount}
            color={badgeColor === 'default' ? 'primary' : badgeColor}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                height: 18,
                minWidth: 18,
                fontWeight: 700,
              },
            }}
          >
            <Icon sx={{ fontSize: 21 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 380,
            maxWidth: '100%',
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
            borderRadius: 2,
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--bg-surface-2)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Pending sync
            </Typography>
            {stats.flushing && (
              <CircularProgress size={14} sx={{ color: PRIMARY }} />
            )}
          </Stack>
          <Chip
            label={tooltip}
            size="small"
            sx={{
              bgcolor: 'rgba(31, 122, 99, 0.06)',
              color: PRIMARY,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
              borderRadius: '6px',
            }}
          />
        </Box>

        <Box sx={{ maxHeight: 380, overflow: 'auto' }}>
          {sortedPending.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No pending actions
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {sortedPending.map((item) => {
                const s = statusLabel(item.status);
                return (
                  <React.Fragment key={item.id}>
                    <Box
                      component="li"
                      sx={{ display: 'block', px: 2.5, py: 1.5 }}
                    >
                      {/* ══════════════════════════════════════════════════════
                          T83 — THE OVERLAP, AND WHY IT IS FIXED BY DELETING CODE.

                          This row used MUI's `secondaryAction`, which positions the
                          button stack `position:absolute; right:16; top:50%`. MUI
                          reserves `paddingRight: 48` on the ListItem to keep text
                          clear of it — and the `sx={{ px: 2.5 }}` on that ListItem
                          overrode the reserve back down to 20px, because `sx` wins
                          the emotion cascade. So the buttons floated, vertically
                          centred, straight across the URL line and the
                          "1d ago · 129 attempts · HTTP 401" line. `mr: 8` (64px) was
                          an attempt to compensate and was ~50px short of the real
                          stack width, and the status Chip in the row above had no
                          margin at all, so "Sign in to retry" sat underneath
                          "Retry"/"Discard".

                          Nothing here is absolutely positioned any more. Four rows
                          in normal flow, buttons last: an overlap is now structurally
                          impossible rather than tuned away. (This is also the shape-24
                          lesson — you cannot fix a collision by adjusting margins when
                          the layout model is what puts the two on top of each other.)
                          The old markup additionally nested a <Stack> (a div) inside
                          ListItemText's <p>, which the HTML parser silently closes.
                          ══════════════════════════════════════════════════════ */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
                          {item.summary || `${item.method} ${item.url}`}
                        </Typography>
                        <Chip
                          label={s.label}
                          size="small"
                          sx={{
                            flexShrink: 0,
                            bgcolor: `${s.color}1A`,
                            color: s.color,
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            height: 18,
                            borderRadius: '6px',
                          }}
                        />
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        title={`${item.method} ${item.url}`}
                        sx={{
                          display: 'block', mt: 0.5,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {item.method} {item.url}
                      </Typography>

                      {/* The error can be a 240-char body slice from the server, and
                          a URL inside it has no spaces to break on — hence
                          overflowWrap, which `noWrap` above deliberately does not get
                          because a truncated URL is more readable than a wrapped one. */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', overflowWrap: 'anywhere' }}
                      >
                        {formatAge(item.createdAt)} · {item.attempts} attempt
                        {item.attempts === 1 ? '' : 's'}
                        {item.lastError ? ` · ${item.lastError}` : ''}
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.75 }}>
                        <Button
                          size="small"
                          disabled={busyId === item.id}
                          onClick={() => handleRetry(item.id)}
                          sx={{ minWidth: 0, textTransform: 'none', fontSize: '0.7rem' }}
                        >
                          Retry
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          disabled={busyId === item.id}
                          onClick={() => handleDiscard(item.id)}
                          sx={{ minWidth: 0, textTransform: 'none', fontSize: '0.7rem' }}
                        >
                          Discard
                        </Button>
                      </Box>
                    </Box>
                    <Divider component="li" />
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default PendingSyncIndicator;
